import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getConsent, setConsent as saveConsent, hasConsentDecision } from '../utils/cookieConsent';

const CookieConsentContext = createContext(null);

// Activate blocked marketing scripts (Google Ads, etc.)
// Finds <script type="text/plain" data-cookie-category="marketing"> and creates live copies
function activateMarketingScripts() {
  const blocked = document.querySelectorAll(
    'script[type="text/plain"][data-cookie-category="marketing"]'
  );
  blocked.forEach((el) => {
    const newScript = document.createElement('script');
    if (el.dataset.cookieSrc) {
      newScript.src = el.dataset.cookieSrc;
      newScript.async = true;
    } else {
      newScript.textContent = el.textContent;
    }
    el.parentNode.insertBefore(newScript, el.nextSibling);
    el.remove();
  });
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => getConsent());
  const [showBanner, setShowBanner] = useState(() => !hasConsentDecision());
  const [showSettings, setShowSettings] = useState(false);

  const updateConsent = useCallback(({ analytics, marketing }) => {
    const saved = saveConsent({ analytics, marketing });
    setConsentState(saved);
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const acceptAll = useCallback(() => {
    updateConsent({ analytics: true, marketing: true });
  }, [updateConsent]);

  const rejectAll = useCallback(() => {
    updateConsent({ analytics: false, marketing: false });
  }, [updateConsent]);

  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Activate scripts when marketing consent is granted (initial load or state change)
  useEffect(() => {
    if (consent?.marketing) {
      activateMarketingScripts();
    }
  }, [consent?.marketing]);

  const value = {
    consent,
    showBanner,
    showSettings,
    updateConsent,
    acceptAll,
    rejectAll,
    openSettings,
    closeSettings,
    hasAnalyticsConsent: consent?.analytics === true,
    hasMarketingConsent: consent?.marketing === true,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
}
